import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import {
  toCampaignSheet,
  type AvailableUser,
  type CampaignSheet,
  type CampaignSummary,
} from './types'

type Schema = components['schemas']

/**
 * Query keys.
 *
 * A campaign is two cache entries, not one, because the API gives it two reads:
 * `sheet` (core, players, characters, notes, the city stub) and `world`
 * (city, factions, locations). The world graph is deep and most campaign opens never
 * show it, so it stays a separate fetch — and therefore a separate key.
 *
 * Mutations patch both, filtered to the sections each entry actually holds; see
 * `patchCampaign` in mutations.ts.
 */
export const campaignKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignKeys.all, 'list'] as const,
  list: (page: number, pageSize: number) => [...campaignKeys.lists(), page, pageSize] as const,
  sheets: () => [...campaignKeys.all, 'sheet'] as const,
  sheet: (id: number) => [...campaignKeys.sheets(), id] as const,
  worlds: () => [...campaignKeys.all, 'world'] as const,
  world: (id: number) => [...campaignKeys.worlds(), id] as const,
  availableUsers: (id: number, search: string) =>
    [...campaignKeys.all, id, 'available-users', search] as const,
}

export interface CampaignListPage {
  items: CampaignSummary[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

/** The campaigns the caller runs or plays in, newest first. Admins see all of them. */
export function useCampaignList(page = 1, pageSize = 50): UseQueryResult<CampaignListPage> {
  return useQuery({
    queryKey: campaignKeys.list(page, pageSize),
    queryFn: async () => {
      const result = await api.get<Schema['CampaignSummaryDtoPagedResult']>('/campaigns', {
        query: { page, pageSize },
      })

      return {
        items: result.items ?? [],
        page: result.page ?? page,
        pageSize: result.pageSize ?? pageSize,
        totalCount: result.totalCount ?? 0,
        totalPages: result.totalPages ?? 0,
      }
    },
  })
}

/** The campaign sheet: core, participants, roster, the caller's notes, the city stub. */
export function useCampaignSheet(campaignId: number): UseQueryResult<CampaignSheet> {
  return useQuery({
    queryKey: campaignKeys.sheet(campaignId),
    enabled: Number.isFinite(campaignId),
    queryFn: async () =>
      toCampaignSheet(await api.get<Schema['CampaignSheetDto']>(`/campaigns/${campaignId}`)),
  })
}

/**
 * The world sheet: the city with its factions and locations.
 *
 * A campaign with no city still answers 200 here — with an all-null city stub and
 * empty faction and location lists — so this is not conditional on the campaign
 * having a world yet.
 */
export function useCampaignWorld(
  campaignId: number,
  enabled = true,
): UseQueryResult<CampaignSheet> {
  return useQuery({
    queryKey: campaignKeys.world(campaignId),
    enabled: enabled && Number.isFinite(campaignId),
    queryFn: async () =>
      toCampaignSheet(await api.get<Schema['CampaignSheetDto']>(`/campaigns/${campaignId}/city`)),
  })
}

/**
 * Users the GM could invite.
 *
 * The API requires a term of at least two characters and 400s below that — deliberately,
 * so this is the one endpoint reaching outside the campaign and it never enumerates the
 * user table. The same floor is applied here as `enabled`, so a one-character search is
 * simply not sent rather than sent and rejected.
 */
export const AVAILABLE_USER_MIN_SEARCH = 2

export function useAvailableUsers(
  campaignId: number,
  search: string,
  enabled = true,
): UseQueryResult<AvailableUser[]> {
  const term = search.trim()

  return useQuery({
    queryKey: campaignKeys.availableUsers(campaignId, term),
    enabled: enabled && term.length >= AVAILABLE_USER_MIN_SEARCH,
    queryFn: () =>
      api.get<AvailableUser[]>(`/campaigns/${campaignId}/available-users`, {
        query: { search: term },
      }),
  })
}

export interface CreateCampaignInput {
  name: string
  /** Creates the city alongside the campaign. Omitted, the campaign starts world-less. */
  cityName?: string | undefined
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateCampaignInput) =>
      toCampaignSheet(
        await api.post<Schema['CampaignSheetDto']>('/campaigns', {
          name: input.name,
          cityName: input.cityName ?? null,
        }),
      ),

    onSuccess: (sheet) => {
      // Creation is not an envelope patch — it returns a whole new sheet and it
      // changes a list this cache holds, so invalidating is correct here in a way
      // it never is for a section mutation.
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
      queryClient.setQueryData(campaignKeys.sheet(sheet.campaignId), sheet)
    },
  })
}

/**
 * Deletes a campaign and everything under it. The only campaign write that answers
 * 204 rather than an envelope — there is no sheet left to patch — so it removes the
 * cache entries instead.
 */
export function useDeleteCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (campaignId: number) => api.delete<void>(`/campaigns/${campaignId}`),

    onSuccess: (_result, campaignId) => {
      queryClient.removeQueries({ queryKey: campaignKeys.sheet(campaignId) })
      queryClient.removeQueries({ queryKey: campaignKeys.world(campaignId) })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}
